import i18n from "../../../../i18n";
import { formatNumberWithDigits } from "../formatNumberWithDigits";
import type { BillingBalance, BillingHistoryItem } from "../../../models/billing/Billing";

/**
 * Подпись справочного значения биллинга (роль, статус, тип операции, событие,
 * продукт). Все они приходят с бэка машинными кодами на латинице; переводы живут
 * в `ru.ts` под `billing.labels.*`.
 *
 * Незнакомый код показываем как есть: лучше увидеть `some_new_type`, чем прочерк
 * — так новый код из биллинга сразу заметен и его можно перевести, а данные в
 * истории операций при этом не теряются.
 */
function labelFor(group: string, code: string | null | undefined, fallback = "—"): string {
  if (!code) return fallback;
  const key = `billing.labels.${group}.${code}`;
  return i18n.global.te(key) ? i18n.global.t(key) : code;
}

export function formatTokens(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0";
  return formatNumberWithDigits(value);
}

export function formatSignedTokens(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0";
  const abs = Math.abs(value);
  if (value < 0) return `−${formatNumberWithDigits(abs)}`;
  if (value > 0) return `+${formatNumberWithDigits(abs)}`;
  return "0";
}

/** Цена в рублях с разделением разрядов: 4900 → "4 900 ₽". */
export function formatRubles(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0 ₽";
  return `${formatNumberWithDigits(Math.round(value))} ₽`;
}

/** Эффективная цена токена: 1.96 → "1,96 ₽". */
export function formatTokenPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toFixed(2).replace(".", ",")} ₽`;
}

export function isFreeTariff(tariff: { monthly_price?: number | null; code?: string | null }): boolean {
  return tariff.code === "free" || tariff.monthly_price === 0;
}

/**
 * Название тарифа для UI: у бесплатного тарифа бэк отдаёт английское "Free" —
 * показываем локализованное «Бесплатно» (freeLabel). Остальные — как есть.
 */
export function tariffNameOrFree(
  tariff: { display_name?: string | null; monthly_price?: number | null; code?: string | null },
  freeLabel: string,
): string {
  if (isFreeTariff(tariff)) return freeLabel;
  const name = tariff.display_name?.trim();
  return name && name.length > 0 ? name : freeLabel;
}

export function formatRole(role: string): string {
  return labelFor("roles", role);
}

export function formatRoleList(roles: string[] | null | undefined): string {
  if (!roles?.length) return "—";
  return roles.map(formatRole).join(" · ");
}

export function formatAccountStatus(status: string | null | undefined): string {
  return labelFor("accountStatuses", status);
}

/** Человекочитаемый статус операции из истории (display_status). */
export function formatBillingStatus(status: string | null | undefined): string {
  return labelFor("operationStatuses", status);
}

export function formatRelationship(rel: string | null | undefined): string {
  return labelFor("relationships", rel, "");
}

export function profileInitial(displayName: string | null | undefined): string {
  const safe = (displayName ?? "").trim();
  if (!safe) return "?";
  return safe.charAt(0).toUpperCase();
}

const RU_MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

function parseIso(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatBillingDateTime(value: string | null | undefined): string {
  const d = parseIso(value);
  if (!d) return "—";
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function formatBillingDate(value: string | null | undefined): string {
  const d = parseIso(value);
  if (!d) return "—";
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/**
 * Дата, до которой действует текущий тариф клиента (ISO-строка или null).
 *
 * Ни одно поле balance не верно во всех случаях: после обычного `apply`
 * `tariff_valid_until` = null, а срок живёт в активном пакете; после `extend`
 * продлевающий пакет стоит в очереди (в balance не виден), а срок — в
 * `tariff_valid_until`. Поэтому берём максимум из `tariff_valid_until` и концов
 * активных базовых (не promo) пакетов. Для free/бессрочного тарифа — null.
 */
export function currentTariffValidUntil(balance: BillingBalance | null): string | null {
  if (!balance) return null;
  const candidates: string[] = [];
  if (balance.tariff_valid_until) candidates.push(balance.tariff_valid_until);
  for (const pkg of balance.packages ?? []) {
    if (pkg.status === "active" && pkg.kind !== "promo" && pkg.end_at) {
      candidates.push(pkg.end_at);
    }
  }
  let latest: { iso: string; time: number } | null = null;
  for (const iso of candidates) {
    const d = parseIso(iso);
    if (!d) continue;
    const time = d.getTime();
    if (latest === null || time > latest.time) latest = { iso, time };
  }
  return latest?.iso ?? null;
}

/** Короткая дата «12 сен». Принимает и готовый Date — для дат без времени, которые нельзя гонять через UTC. */
/** Сколько израсходовано из выданного по активным пакетам (см. activePackagesUsage). */
export interface BillingPackagesUsage {
  charged: number;
  granted: number;
  percent: number;
}

/**
 * Расход по активным пакетам клиента: сколько из выданного уже израсходовано.
 *
 * Числитель и знаменатель обязаны иметь один охват — оба считаются по активным
 * пакетам (`tokens_charged` и `initial_grant`). Кумулятивный
 * `temporary_charged_today` сюда не годится: он тащит списания уже закрытых
 * пакетов и после смены тарифа рисует «пакет почти исчерпан» у клиента,
 * который только что получил новый (BIL-45).
 *
 * `null` — показывать нечего: активных пакетов нет либо по ним ничего не выдано.
 */
export function activePackagesUsage(balance: BillingBalance | null): BillingPackagesUsage | null {
  if (!balance) return null;
  let charged = 0;
  let granted = 0;
  for (const pkg of balance.packages ?? []) {
    if (pkg.status !== "active") continue;
    charged += Math.max(0, Number(pkg.tokens_charged ?? 0));
    granted += Math.max(0, Number(pkg.initial_grant ?? 0));
  }
  if (granted <= 0) return null;

  const capped = Math.min(charged, granted);
  return { charged: capped, granted, percent: Math.round((capped / granted) * 100) };
}

export function formatBillingDateShort(value: string | Date | null | undefined): string {
  const d = parseIso(value);
  if (!d) return "—";
  return `${d.getDate()} ${RU_MONTHS_SHORT[d.getMonth()]}`;
}

/** Тип операции из истории (operation_type), напр. `settle` → «Закрытие резерва». */
export function formatOperationType(type: string | null | undefined): string {
  return labelFor("operations", type);
}

/**
 * Событие, за которое списаны токены (event_type), напр. `group-search` →
 * «Групповая проценка». Набор событий задан точками списания в инстансах
 * (SER-1295): поиск, оформление заказа, кроссы, каталоги по VIN/госномеру.
 */
export function formatBillingEventType(eventType: string | null | undefined): string {
  return labelFor("events", eventType);
}

/**
 * Продукт-инициатор операции (product_id) — фронт, с которого пришёл запрос:
 * `lk` (личный кабинет), `onec` (1С), `sto` (СТО), `api-eplex` (прямой API).
 */
export function formatBillingProduct(productId: string | null | undefined): string {
  return labelFor("products", productId);
}

// операции, уменьшающие баланс; используется только как фоллбек при отсутствии балансов
const NEGATIVE_OPERATION_TYPES = new Set(["freeze", "charge"]);

/**
 * Знаковое изменение баланса по записи истории. `amount_tokens` у бэка — беззнаковый модуль
 * (направление кодируется в operation_type), поэтому сумму для отображения считаем как
 * дельту баланса: freeze −frozen, settle +released (заморозка уже снята с баланса, при
 * закрытии резерва возвращается неиспользованное), accrual +amount. Дельта самосогласована:
 * freeze −32 и settle +6 в сумме дают фактическое списание −26.
 */
export function billingHistoryAmount(item: BillingHistoryItem): number {
  const { balance_before, balance_after } = item;
  if (typeof balance_before === "number" && typeof balance_after === "number") {
    return balance_after - balance_before;
  }

  const amount = Number(item.amount_tokens ?? 0);
  return NEGATIVE_OPERATION_TYPES.has(item.operation_type) ? -amount : amount;
}
